import {
  CREDIT_CARD_PROVIDERS_CVV_LENGTH,
  CREDIT_CARD_STARTING_DIGITS,
} from 'lib/settings/cards'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Control, useForm } from 'react-hook-form'
import AccountSection from '../AccountSection/AccountSection'
import Button from '../Button/Button'
import LoadingButton from '../Button/LoadingButton'
import { CardExpirationInput, CardNumberInput, TextInput } from '../Form'
import Notification from '../Notification/Notification'

export interface AccountPaymentMethod {
  cvc: string
  expirationDate: string
  number: string
}

export interface PreviousPaymentMethod {
  cardBrand: string
  displayName: string
  expirationDate: string
  id: string
  last4: string
}

export interface AccountPaymentInformationProps {
  /**
   * Number of payment methods on file
   */
  count?: number
  /**
   * Error object
   */
  error?: Error | null
  /**
   * Loading state
   */
  isProcessing?: boolean
  /**
   * Previous payment method
   */
  previousPaymentMethod?: PreviousPaymentMethod
  /**
   * Callback on card deletion
   */
  onDelete: () => void
  /**
   * Callback on card save
   */
  onSave: (data: AccountPaymentMethod) => void
}

export default function AccountPaymentInformation({
  count = 1,
  error = null,
  isProcessing = false,
  previousPaymentMethod,
  onDelete,
  onSave,
}: Readonly<AccountPaymentInformationProps>) {
  const defaultExpirationDate = useMemo(() => {
    if (!previousPaymentMethod?.expirationDate) return undefined

    const date = new Date(previousPaymentMethod?.expirationDate)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)

    return `${month}/${year}`
  }, [previousPaymentMethod?.expirationDate])

  const { control, handleSubmit, reset, watch } = useForm<AccountPaymentMethod>(
    {
      defaultValues: { expirationDate: defaultExpirationDate || '' },
    },
  )

  const [cardType, setCardType] = useState<string | null>(null)
  const [isEdit, setIsEdit] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cardNumber = watch('number')
  const expirationDate = watch('expirationDate')

  const description = useMemo(
    () =>
      previousPaymentMethod
        ? `${previousPaymentMethod.cardBrand} - ${previousPaymentMethod.last4}`
        : '',
    [previousPaymentMethod],
  )

  const handleCancel = useCallback(() => {
    setIsEdit(false)
    reset()
  }, [reset])

  const handleEditCard = useCallback(() => {
    setIsEdit(true)
  }, [])

  const submitForm = useCallback(
    (data: AccountPaymentMethod) => {
      setIsSubmitting(true)
      onSave(data)
    },
    [onSave],
  )

  useEffect(() => {
    if (!cardNumber || cardNumber.length === 0) {
      setCardType(null)
      return
    }

    const firstDigit = cardNumber.charAt(
      0,
    ) as unknown as keyof typeof CREDIT_CARD_STARTING_DIGITS
    const cardType = CREDIT_CARD_STARTING_DIGITS[firstDigit]
    setCardType(cardType)
  }, [cardNumber])

  useEffect(() => {
    setIsEdit(!previousPaymentMethod)
    setIsMounted(true)
  }, [previousPaymentMethod])

  useEffect(() => {
    if (error && isSubmitting) {
      setIsEdit(true)
      setIsSubmitting(false)
    }
  }, [error, isSubmitting])

  useEffect(() => {
    if (!isProcessing && isSubmitting) {
      setIsEdit(false)
      setIsSubmitting(false)
    }
  }, [isProcessing, isSubmitting])

  return (
    <AccountSection title="Manage payment information">
      <div className="relative">
        {!isMounted ? (
          <span className="text-sm text-gray-600">Loading...</span>
        ) : (
          <>
            <form
              onSubmit={handleSubmit(submitForm)}
              className="flex flex-col gap-y-4 md:gap-y-6"
            >
              {!error?.message && !isEdit && (
                <>
                  <span className="type-body-6">
                    Current credit card on file: ({count} on file)
                  </span>
                  <span className="type-body-6">{description}</span>
                  <div className="flex flex-col gap-y-2 md:flex-row md:gap-x-4">
                    <Button onClick={handleEditCard}>Edit card on file</Button>
                    <Button onClick={onDelete} variant="secondary">
                      Delete card on file
                    </Button>
                  </div>
                </>
              )}
              {(error?.message || isEdit) && (
                <>
                  {error?.message && (
                    <Notification title={error?.message} type={'urgent'} />
                  )}
                  <CardNumberInput
                    control={control as unknown as Control}
                    name={'number'}
                    label="Card number"
                    placeholder="Card number"
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a valid input',
                      },
                    }}
                    className="[&_input]:mb-0 [&_p]:mt-2"
                  />
                  <div className="flex flex-col gap-y-4 md:gap-y-6 lg:gap-y-0 lg:flex-row lg:gap-x-8">
                    <CardExpirationInput
                      control={control as unknown as Control}
                      defaultValue={
                        previousPaymentMethod
                          ? expirationDate || defaultExpirationDate
                          : undefined
                      }
                      rules={{ required: 'Please enter a valid input' }}
                      label="Expiration date (mm/yy)"
                      placeholder="Expiration date (mm/yy)"
                      name="expirationDate"
                      className="[&_input]:mb-0 [&_p]:mt-2 w-full"
                    />
                    <TextInput
                      control={control as unknown as Control}
                      name="cvc"
                      label="Security code"
                      className="[&>div]:mb-0 [&_p]:mt-2 w-full"
                      placeholder="Security code"
                      rules={{
                        required: 'Please enter a valid input',
                        pattern: {
                          message: 'Please enter a valid CVC code',
                          value: /^[0-9]*$/,
                        },
                        validate: (val: string) => {
                          if (cardType) {
                            const numberOfDigits =
                              CREDIT_CARD_PROVIDERS_CVV_LENGTH[cardType]

                            if (val.length !== numberOfDigits) {
                              return `CVV must be ${numberOfDigits} digits`
                            }
                          }
                        },
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-y-2 md:flex-row md:gap-x-4">
                    <LoadingButton
                      className="min-w-[120px]"
                      loading={isProcessing}
                      type="submit"
                    >
                      Save
                    </LoadingButton>
                    {previousPaymentMethod && (
                      <Button
                        className="min-w-[120px]"
                        disabled={isProcessing}
                        onClick={handleCancel}
                        variant="secondary"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </>
              )}
            </form>
          </>
        )}
      </div>
    </AccountSection>
  )
}
